"use client";

import React, { forwardRef } from "react";
import Image from "next/image";
import { Loader2, XCircle, Search, History, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchResult } from "@/store/search-store";

interface SearchResultsProps {
  query: string;
  results: SearchResult[];
  isLoading: boolean;
  error: string | null;
  recentSearches: string[];
  onItemClick: (url: string) => void;
  onRecentSearchClick: (query: string) => void;
  onClearRecentSearches: () => void;
}

const SearchResults = forwardRef<HTMLDivElement, SearchResultsProps>(
  (props, ref) => {
    const {
      query,
      results,
      isLoading,
      error,
      recentSearches,
      onItemClick,
      onRecentSearchClick,
      onClearRecentSearches,
    } = props;

    const hasRecentSearches = recentSearches.length > 0;

    return (
      <div
        ref={ref}
        className='absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-md border border-muted bg-background shadow-lg'
        style={{ maxHeight: "80vh" }}
      >
        <div className='flex items-center justify-between border-b border-muted px-4 py-2'>
          {query ? (
            <span className='text-sm font-medium text-muted-foreground'>
              {isLoading
                ? "Searching..."
                : results.length > 0
                  ? `${results.length} result${results.length === 1 ? "" : "s"}`
                  : "No results"}
            </span>
          ) : hasRecentSearches ? (
            <span className='text-sm font-medium text-muted-foreground'>
              Recent Searches
            </span>
          ) : (
            <span className='text-sm font-medium text-muted-foreground'>
              Start typing to search
            </span>
          )}

          {hasRecentSearches && !query && (
            <Button
              variant='ghost'
              size='sm'
              className='h-auto p-1 text-xs text-muted-foreground hover:text-foreground'
              onClick={onClearRecentSearches}
            >
              Clear
            </Button>
          )}
        </div>

        <div className='max-h-[calc(80vh-40px)] overflow-y-auto'>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          ) : error ? (
            <div className='flex flex-col items-center justify-center py-8'>
              <XCircle className='h-6 w-6 text-destructive mb-2' />
              <p className='text-sm text-muted-foreground'>{error}</p>
            </div>
          ) : query && results.length > 0 ? (
            <div>
              {results.map((item) => (
                <div
                  key={item.id}
                  className='flex items-start gap-3 border-b border-muted px-4 py-3 hover:bg-muted cursor-pointer'
                  onClick={() => onItemClick(item.url)}
                >
                  <div className='flex-shrink-0 bg-muted rounded-md h-12 w-12 overflow-hidden flex items-center justify-center'>
                    {item.image && item.image.length > 0 ? (
                      <Image
                        src={item.image[0]}
                        alt={item.title}
                        width={48}
                        height={48}
                        className='object-contain'
                      />
                    ) : (
                      <div className='text-xl text-muted-foreground font-light'>
                        ?
                      </div>
                    )}
                  </div>
                  <div className='flex-1 overflow-hidden'>
                    <div className='text-sm font-medium line-clamp-1'>
                      {item.title}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {item.part_number}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : !query && hasRecentSearches ? (
            <div>
              {recentSearches.map((search, index) => (
                <div
                  key={index}
                  className='flex items-center justify-between px-4 py-2 hover:bg-muted cursor-pointer'
                  onClick={() => onRecentSearchClick(search)}
                >
                  <div className='flex items-center gap-2'>
                    <History className='h-4 w-4 text-muted-foreground' />
                    <span className='text-sm'>{search}</span>
                  </div>
                  <button
                    className='rounded-full hover:bg-background p-1'
                    onClick={(e) => {
                      e.stopPropagation();
                      const newRecentSearches = recentSearches.filter(
                        (s) => s !== search
                      );
                      if (newRecentSearches.length === 0) {
                        onClearRecentSearches();
                      } else {
                        // Remove just this item
                        localStorage.setItem(
                          "recentSearches",
                          JSON.stringify(newRecentSearches)
                        );
                        window.dispatchEvent(
                          new Event("storage", { bubbles: true })
                        );
                      }
                    }}
                  >
                    <XIcon className='h-3 w-3 text-muted-foreground' />
                  </button>
                </div>
              ))}
            </div>
          ) : query ? (
            <div className='flex items-center justify-center py-8'>
              <p className='text-sm text-muted-foreground'>
                No results found for &quot;{query}&quot;
              </p>
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center py-8'>
              <Search className='h-6 w-6 text-muted-foreground mb-2' />
              <p className='text-sm text-muted-foreground'>
                Type to search products...
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

SearchResults.displayName = "SearchResults";

export default SearchResults;
